import { useState, useEffect } from 'react';
import { apiClient } from '../../../api/client';

// IMPORTANT: Use apiClient instead of axios directly to get auth token automatically

export interface Account {
  id: string;             // ✅ Primary ID (from backend)
  account_id: string;     // ✅ Alias for compatibility with workflow config
  name: string;
  platform: 'facebook' | 'instagram' | 'threads' | 'tiktok' | 'zalo' | 'linkedin';
  status: string;
  profile_url?: string;
  managed_fanpage_urls?: string[]; // Facebook: fanpage profile URLs this account can switch to
}

export const useAccounts = (platform?: string) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        setError(null);

        // ✅ Use apiClient.getMyAccounts() which has auth token
        const accountsData = await apiClient.getMyAccounts();
        
        console.log('📦 Fetched accounts:', accountsData.length);
        console.log('📦 First account:', accountsData[0]);
        
        // Filter by platform client-side (fast, no extra API call)
        let filteredAccounts = accountsData;
        if (platform) {
          filteredAccounts = accountsData.filter((acc: any) => acc.platform === platform);
          console.log(`🔎 After ${platform} filter:`, filteredAccounts.length, 'accounts');
        }
        
        // Transform to include name field (prioritize full_name over username)
        const transformedAccounts = filteredAccounts.map((acc: any) => ({
          ...acc,
          id: acc.id,                    // ✅ Keep backend id
          account_id: acc.id,            // ✅ Alias for workflow config compatibility
          name: acc.full_name || acc.username || acc.email || 'Unnamed Account',
        }));

        setAccounts(transformedAccounts);
      } catch (err: any) {
        console.error('❌ Failed to fetch accounts:', err);
        setError(err.message || 'Failed to fetch accounts');
        setAccounts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [platform]);

  return { accounts, loading, error };
};
